import { LRLanguage, LanguageSupport, foldInside, foldNodeProp } from "@codemirror/language";
import { NodeProp } from "@lezer/common";
import { styleTags, tags as t } from "@lezer/highlight";

import { languageLoadedListener } from "../../language.js";
import { parser } from "./latex.mjs";
import * as termsModule from "./latex.terms.mjs";
import { closeBracketConfig } from "./close-bracket-config.js";
import { commentIsOpenFold, findClosingFoldComment, getFoldRange } from "./comments.js";
import { Tokens } from "./tokens.js";

const styleOverrides = {
    DocumentClassCtrlSeq: t.keyword,
    UsePackageCtrlSeq: t.keyword,
    CiteCtrlSeq: t.keyword,
    CiteStarrableCtrlSeq: t.keyword,
    RefCtrlSeq: t.keyword,
    RefStarrableCtrlSeq: t.keyword,
    LabelCtrlSeq: t.keyword,
};

const styleEntry = (token, defaultStyle) => [token, styleOverrides[token] || defaultStyle];

const Styles = {
    ctrlSeq: Object.fromEntries(Tokens.ctrlSeq.map((token) => styleEntry(token, t.tagName))),
    ctrlSym: Object.fromEntries(Tokens.ctrlSym.map((token) => styleEntry(token, t.literal))),
    envName: Object.fromEntries(Tokens.envName.map((token) => styleEntry(token, t.attributeValue))),
};

const typeMap = {
    PartCtrlSeq: ["$SectioningCtrlSeq"],
    ChapterCtrlSeq: ["$SectioningCtrlSeq"],
    SectionCtrlSeq: ["$SectioningCtrlSeq"],
    SubSectionCtrlSeq: ["$SectioningCtrlSeq"],
    SubSubSectionCtrlSeq: ["$SectioningCtrlSeq"],
    ParagraphCtrlSeq: ["$SectioningCtrlSeq"],
    SubParagraphCtrlSeq: ["$SectioningCtrlSeq"],
    HrefCommand: ["$CommandTooltipCommand"],
    Include: ["$CommandTooltipCommand"],
    Input: ["$CommandTooltipCommand"],
    Subfile: ["$CommandTooltipCommand"],
    Ref: ["$CommandTooltipCommand"],
    UrlCommand: ["$CommandTooltipCommand"],
    TextBoldCommand: ["$ToggleTextFormattingCommand"],
    TextItalicCommand: ["$ToggleTextFormattingCommand"],
    TextSmallCapsCommand: ["$OtherTextFormattingCommand"],
    TextTeletypeCommand: ["$OtherTextFormattingCommand"],
    TextMediumCommand: ["$OtherTextFormattingCommand"],
    TextSansSerifCommand: ["$OtherTextFormattingCommand"],
    TextSuperscriptCommand: ["$OtherTextFormattingCommand"],
    TextSubscriptCommand: ["$OtherTextFormattingCommand"],
    StrikeOutCommand: ["$OtherTextFormattingCommand"],
    EmphasisCommand: ["$OtherTextFormattingCommand"],
    UnderlineCommand: ["$OtherTextFormattingCommand"],
};

const configuredParser = parser.configure({
    props: [
        foldNodeProp.add({
            Comment: (node, state) => {
                if (commentIsOpenFold(node, state)) {
                    const closingFoldNode = findClosingFoldComment(node, state);
                    if (closingFoldNode) {
                        return getFoldRange(node, closingFoldNode, state);
                    }
                }
                return null;
            },
            Group: foldInside,
            NonEmptyGroup: foldInside,
            TextArgument: foldInside,
            $Environment: (node) => node.getChild("Content"),
            $Section: (node) => {
                const lastChild = node.resolveInner(node.to, -1);
                const content = node.getChild("Content");
                if (!content) {
                    return null;
                }
                if (lastChild.type.is(termsModule.NewLine)) {
                    return { from: content.from, to: lastChild.from };
                }
                if (lastChild.type.is(termsModule.Whitespace)) {
                    let sibling = lastChild.prevSibling;
                    while (sibling?.type.is(termsModule.Whitespace)) {
                        sibling = sibling.prevSibling;
                    }
                    if (sibling?.type.is(termsModule.NewLine)) {
                        return { from: content.from, to: sibling.from };
                    }
                }
                if (lastChild.type.is(termsModule.BlankLine)) {
                    return { from: content.from, to: lastChild.to - 1 };
                }
                return content;
            },
        }),
        NodeProp.group.add((type) => {
            const types = [];

            if (Tokens.ctrlSeq.includes(type.name) || Tokens.ctrlSym.includes(type.name)) {
                types.push("$CtrlSeq");
                if (Tokens.ctrlSym.includes(type.name)) {
                    types.push("$CtrlSym");
                }
            } else if (Tokens.envName.includes(type.name)) {
                types.push("$EnvName");
            } else if (type.name.endsWith("Command")) {
                types.push("$Command");
            } else if (type.name.endsWith("Argument")) {
                types.push("$Argument");
                if (type.name.endsWith("TextArgument") || type.is("SectioningArgument")) {
                    types.push("$TextArgument");
                }
            } else if (type.name.endsWith("Brace")) {
                types.push("$Brace");
            }

            if (type.name in typeMap) {
                types.push(...typeMap[type.name]);
            }

            return types.length > 0 ? types : undefined;
        }),
        styleTags({
            ...Styles.ctrlSeq,
            ...Styles.ctrlSym,
            ...Styles.envName,
            "HrefCommand/ShortTextArgument/ShortArg/...": t.link,
            "HrefCommand/UrlArgument/...": t.monospace,
            "CtrlSeq Csname": t.tagName,
            "DocumentClass/OptionalArgument/ShortOptionalArg/...": t.attributeValue,
            "DocumentClass/ShortTextArgument/ShortArg/Normal": t.typeName,
            "ListEnvironment/BeginEnv/OptionalArgument/...": t.monospace,
            Comment: t.comment,
            Number: t.number,
            OpenBrace: t.brace,
            CloseBrace: t.brace,
            OpenBracket: t.squareBracket,
            CloseBracket: t.squareBracket,
            Dollar: t.string,
            Math: t.string,
            "Math/MathChar": t.string,
            "Math/MathSpecialChar": t.string,
            "Math/Number": t.string,
            "MathGroup/OpenBrace MathGroup/CloseBrace": t.string,
            "MathTextCommand/TextArgument/OpenBrace MathTextCommand/TextArgument/CloseBrace": t.string,
            "MathOpening/LeftCtrlSeq MathClosing/RightCtrlSeq MathUnknownCommand/CtrlSeq MathTextCommand/CtrlSeq":
                t.literal,
            MathDelimiter: t.literal,
            DoubleDollar: t.keyword,
            Tilde: t.keyword,
            Ampersand: t.keyword,
            LineBreakCtrlSym: t.keyword,
            "UsePackage/OptionalArgument/ShortOptionalArg/Normal": t.attributeValue,
            "UsePackage/ShortTextArgument/ShortArg/Normal": t.tagName,
            "Affiliation/OptionalArgument/ShortOptionalArg/Normal": t.attributeValue,
            "Affil/OptionalArgument/ShortOptionalArg/Normal": t.attributeValue,
            "LiteralArgContent VerbContent VerbatimContent LstInlineContent": t.string,
            "NewCommand/LiteralArgContent": t.typeName,
            "LabelArgument/ShortTextArgument/ShortArg/...": t.attributeValue,
            "RefArgument/ShortTextArgument/ShortArg/...": t.attributeValue,
            "BibKeyArgument/ShortTextArgument/ShortArg/...": t.attributeValue,
            "ShortTextArgument/ShortArg/Normal": t.monospace,
            "UrlArgument/LiteralArgContent": [t.attributeValue, t.url],
            "FilePathArgument/LiteralArgContent": t.attributeValue,
            "BareFilePathArgument/SpaceDelimitedLiteralArgContent": t.attributeValue,
            TrailingContent: t.comment,
            "Item/OptionalArgument/ShortOptionalArg/...": t.strong,
        }),
    ],
});

export const ManuscriptLatexLanguage = LRLanguage.define({
    name: "latex",
    parser: configuredParser,
    languageData: {
        commentTokens: { line: "%" },
        closeBrackets: closeBracketConfig,
    },
});

export function manuscriptLatex() {
    return new LanguageSupport(ManuscriptLatexLanguage, [languageLoadedListener()]);
}
